import { StorageAuthError, StorageError } from '../../errors.js';
const DRIVE_FILES = 'https://www.googleapis.com/drive/v3/files';
const FOLDER_MIME = 'application/vnd.google-apps.folder';
const PROVIDER_ID = 'google-drive';
function authHeaders(token) {
    return { Authorization: `Bearer ${token}` };
}
async function ensureOk(response, op) {
    if (response.ok)
        return;
    if (response.status === 401 || response.status === 403) {
        throw new StorageAuthError(PROVIDER_ID, op);
    }
    const body = await response.text().catch(() => '');
    throw new StorageError(`drive ${op} failed (${response.status}): ${body}`, PROVIDER_ID, op);
}
/** Returns the id of the first matching folder under `parentId` (default: root). */
export async function findFolderByName(token, name, parentId = 'root') {
    const safeName = name.replace(/'/g, "\\'");
    const url = new URL(DRIVE_FILES);
    url.searchParams.set('q', `name='${safeName}' and mimeType='${FOLDER_MIME}' and '${parentId}' in parents and trashed=false`);
    url.searchParams.set('fields', 'files(id,name)');
    url.searchParams.set('pageSize', '10');
    const response = await fetch(url, { headers: authHeaders(token) });
    await ensureOk(response, 'findFolderByName');
    const data = (await response.json());
    return data.files?.[0]?.id ?? null;
}
/** Creates a folder named `name` under `parentId` and returns its id. */
export async function createFolder(token, name, parentId = 'root', appProperties) {
    const meta = {
        name,
        mimeType: FOLDER_MIME,
        parents: [parentId],
    };
    if (appProperties)
        meta.appProperties = appProperties;
    const response = await fetch(`${DRIVE_FILES}?fields=id`, {
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(meta),
    });
    await ensureOk(response, 'createFolder');
    const data = (await response.json());
    return data.id;
}
/**
 * Look up a folder by an `appProperties` key/value pair under `parentId`.
 * Identity-stable across renames; the canonical way to anchor a Drive folder
 * to a logical record id.
 */
export async function findFolderByAppProperty(token, parentId, propKey, propValue) {
    const url = new URL(DRIVE_FILES);
    url.searchParams.set('q', `mimeType='${FOLDER_MIME}' and '${parentId}' in parents and trashed=false ` +
        `and appProperties has { key='${escapeQ(propKey)}' and value='${escapeQ(propValue)}' }`);
    url.searchParams.set('fields', 'files(id,name,appProperties)');
    url.searchParams.set('pageSize', '10');
    const response = await fetch(url, { headers: authHeaders(token) });
    await ensureOk(response, 'findFolderByAppProperty');
    const data = (await response.json());
    return data.files?.[0] ?? null;
}
/** Look up any file (folder or not) under `parentId` by an `appProperties` match. */
export async function findFileByAppProperty(token, parentId, propKey, propValue) {
    const url = new URL(DRIVE_FILES);
    url.searchParams.set('q', `'${parentId}' in parents and trashed=false ` +
        `and appProperties has { key='${escapeQ(propKey)}' and value='${escapeQ(propValue)}' }`);
    url.searchParams.set('fields', 'files(id,name,appProperties)');
    url.searchParams.set('pageSize', '10');
    const response = await fetch(url, { headers: authHeaders(token) });
    await ensureOk(response, 'findFileByAppProperty');
    const data = (await response.json());
    return data.files?.[0] ?? null;
}
/** PATCH-update arbitrary file/folder metadata (name and/or appProperties). */
export async function patchFileMetadata(token, fileId, meta) {
    const response = await fetch(`${DRIVE_FILES}/${encodeURIComponent(fileId)}?fields=id`, {
        method: 'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(meta),
    });
    await ensureOk(response, 'patchFileMetadata');
}
function escapeQ(s) {
    return s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}
/** Returns true if the folder exists and is not trashed. */
export async function validateFolder(token, folderId) {
    const url = `${DRIVE_FILES}/${encodeURIComponent(folderId)}?fields=id,trashed`;
    const response = await fetch(url, { headers: authHeaders(token) });
    if (response.status === 404)
        return false;
    if (response.status === 401 || response.status === 403) {
        throw new StorageAuthError(PROVIDER_ID, 'validateFolder');
    }
    if (!response.ok)
        return false;
    const data = (await response.json());
    return data.trashed !== true;
}
export function folderUrl(folderId) {
    return `https://drive.google.com/drive/folders/${folderId}`;
}
/** Renames the file or folder at `fileId`. */
export async function renameFile(token, fileId, newName) {
    const response = await fetch(`${DRIVE_FILES}/${encodeURIComponent(fileId)}?fields=id`, {
        method: 'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
    });
    await ensureOk(response, 'renameFile');
}
/**
 * Walk a path of folder names under `rootId`, creating any missing folders.
 * Returns the leaf folder's id. Empty path returns rootId.
 */
export async function findOrCreateFolderPath(token, rootId, segments) {
    let current = rootId;
    for (const seg of segments) {
        const found = await findFolderByName(token, seg, current);
        current = found ?? (await createFolder(token, seg, current));
    }
    return current;
}
//# sourceMappingURL=drive-folders.js.map